import useAxios from "axios-hooks";
import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Flex,
  Text,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Icon,
  VStack,
} from "@chakra-ui/react";
import { MdEvStation, MdPowerOff } from "react-icons/md";
import { useRecoilState, useSetRecoilState } from "recoil";

import { billingPlanTableHeaders } from "../variables/tableHeaders";
import Card from "components/card/Card";
import LoadingSpinner from "components/feedback/LoadingSpinner";
import { chargePointState } from "atoms/chargePointState";
import { toastState } from "atoms/toastState";
import Configurations from "../components/Configurations";
import logger from "infra/logger";
import Billing from "../components/Billing";
import RoleBasedRender from "components/wrappers/roleBasedRender";
import { onlyUAdmins } from "utils/accessLevels";
import { onlyAdmins } from "utils/accessLevels";
import PrivateBilling from "../components/PrivateBilling";
import BillingPanel from "./BillingPanel";

const BillingPlan = ({ id }) => {
  const [chargePoints, setChargePoints] = useRecoilState(chargePointState);
  const [updateData, setUpdateData] = useState([]);
  const setToast = useSetRecoilState(toastState);
  const [updating, setUpdating] = useState(false);

  const [{ data, loading, error }, execute] = useAxios(
    { method: "GET", url: `/api/stations/forLocation/${id}` },
    { manual: true }
  );

  const [
    { data: upData, loading: upLoading, error: upError },
    updateChargePoint,
  ] = useAxios({}, { manual: true });

  const fetchChargePoints = async () => {
    try {
      const response = await execute();
      const result = response.data.data;
      console.log(result);
      setChargePoints(result);
      setToast({
        status: "success",
        message: "Charge Points fetched successfully",
        title: "Success",
        position: "top-right",
        duration: 5000,
      });
      logger.log(result);
    } catch (err) {
      logger.log(err);
      setToast({
        title: "Error fetching charge points",
        status: "error",
        duration: 5000,
        iseOpen: true,
      });
    }
  };

  const addToUpdateList = async (dat) => {
    const index = updateData.findIndex((item) => item.Id === dat.Id);
    if (index !== -1) {
      setUpdateData((prev) => {
        const newArray = [...prev];
        newArray[index] = dat; // Replace the item at the found index with the new data
        return newArray;
      });
    } else {
      setUpdateData((prev) => {
        return [...prev, dat];
      });
    }
  };

  const updateChargePointsList = async () => {
    setUpdating(true);
    if (updateData.length > 0) {
      for (let i = 0; i < updateData.length; i++) {
        const pl = updateData[i];

        if (isNaN(pl.payload.Tariff) && pl.payload.TaxEnabled === undefined) {
          setToast({
            title: "Please enter valid Tariff",
            status: "warning",
            duration: 5000,
            isOpen: true,
          });
          setUpdating(false);
          continue;
        }

        if (chargePoints[i].Tariff !== pl.payload.Tariff) {
          try {
            const response = await updateChargePoint({
              method: "PATCH",
              url: `/api/chargePoint/update/${pl.Id}`,
              data: pl.payload,
            });
            setToast({
              title: "Billing plan updated successfully",
              status: "success",
              duration: 5000,
              isOpen: true,
            });
          } catch (error) {
            logger.log(error);
            setToast({
              title: "Error updating Billing plan",
              status: "error",
              duration: 5000,
              isOpen: true,
            });
          }
        } else {
          setToast({
            title: "Nothing New to Update",
            status: "warning",
            duration: 5000,
            isOpen: true,
          });
          setUpdating(false);
          continue;
        }
      }
      logger.log("updating", updateData);
      await fetchChargePoints();
      setUpdating(false);
      setUpdateData([]);
    } else {
      setToast({
        title: "Nothing to update",
        status: "warning",
        duration: 5000,
        isOpen: true,
      });
      setUpdating(false);
    }
  };

  useEffect(() => {
    fetchChargePoints();
  }, []);

  return loading || updating ? (
    <LoadingSpinner />
  ) : (
    <>
      {chargePoints?.length > 0 ? (
        <RoleBasedRender roles={onlyAdmins}>
          <Card
            mt={5}
            display={"flex"}
            justifyContent={"center"}
            alignItems={"center"}
            flexDirection="column"
            p={4}
          >
            <Box width="100%" mb={4}>
              {/* <Text fontSize="xl" fontWeight="bold" mb={3}>
                Station Configuration
              </Text> */}
              <Tabs
                isLazy={true}
                variant="unstyled"
                colorScheme="brand"
                width="100%"
              >
                <Box
                  className="glass-tab-container"
                  borderRadius="15px"
                  mb={4}
                  width="100%"
                  p={1}
                  backgroundColor="rgba(255, 255, 255, 0.6)"
                  backdropFilter="blur(12px)"
                  border="1px solid rgba(35, 169, 121, 0.15)"
                  boxShadow="0 8px 32px 0 rgba(31, 38, 135, 0.07)"
                >
                  <TabList gap={2}>
                    {chargePoints.map((station) => (
                      <Tab
                        key={station.Id}
                        _selected={{
                          color: "brand.500",
                          bg: "rgba(35, 169, 121, 0.15)",
                        }}
                        _hover={{
                          bg: "rgba(35, 169, 121, 0.08)",
                        }}
                        borderRadius="8px"
                        transition="all 0.3s ease"
                        py={3}
                        px={4}
                      >
                        <Flex align="center">
                          <Icon as={MdEvStation} mr={2} />
                          <Text>{station.StationId}</Text>
                        </Flex>
                      </Tab>
                    ))}
                  </TabList>
                </Box>

                <TabPanels>
                  {chargePoints.map((station) => (
                    <TabPanel key={station.Id} p={0}>
                      <BillingPanel
                        id={station.Id}
                        station={station}
                        refresh={fetchChargePoints}
                      />
                      {/* <Configurations id={station.Id} station={station} /> */}
                    </TabPanel>
                  ))}
                </TabPanels>
              </Tabs>
            </Box>
          </Card>
        </RoleBasedRender>
      ) : (
        <Card
          mt={5}
          display="flex"
          justifyContent="center"
          alignItems="center"
          p={8}
          minH="400px"
        >
          <VStack
            spacing={6}
            p={8}
            borderRadius="20px"
            backgroundColor="rgba(255, 255, 255, 0.6)"
            backdropFilter="blur(12px)"
            border="1px solid rgba(35, 169, 121, 0.15)"
            boxShadow="0 8px 32px 0 rgba(31, 38, 135, 0.07)"
            maxW="500px"
            textAlign="center"
          >
            <Box
              position="relative"
              display="inline-flex"
              alignItems="center"
              justifyContent="center"
            >
              <Box
                position="absolute"
                w="120px"
                h="120px"
                borderRadius="50%"
                bg="rgba(35, 169, 121, 0.1)"
                filter="blur(20px)"
                animation="pulse 2s ease-in-out infinite"
              />
              <Icon
                as={MdPowerOff}
                w="60px"
                h="60px"
                color="brand.500"
                opacity={0.8}
                position="relative"
                zIndex={1}
              />
            </Box>

            <VStack spacing={2}>
              <Text
                fontSize="xl"
                fontWeight="bold"
                color="gray.700"
                letterSpacing="tight"
              >
                No Chargers Found
              </Text>
              <Text fontSize="sm" color="gray.500" maxW="350px">
                There are currently no chargers configured for this location.
                Add chargers to manage billing plans.
              </Text>
            </VStack>
          </VStack>

          <style>
            {`
              @keyframes pulse {
                0%, 100% {
                  opacity: 0.6;
                  transform: scale(1);
                }
                50% {
                  opacity: 0.8;
                  transform: scale(1.1);
                }
              }
            `}
          </style>
        </Card>
      )}
    </>
  );
};

export default BillingPlan;
